// PAY2-S9 (docs/build-briefs/PAY2-S9-royalty-one-view.md; DEC-405, DEC-406, DEC-420): the location's OWN royalty view, drawn
// into the Reports body. It replaces the Royalty statement table and the Charges panel with one: a month picker, one row for
// the month (net sales, rate lines, royalty due, adjustments, amount charged, carried, collection with its debit date,
// dispute), then the Adjustments and Disputes lists that carry detail a row cannot (kind, netted in, reason, the franchisor's
// note), and one Export (Statement, Charges, Adjustments, Disputes). A location's Admin disputes a charge from the row; any
// viewer with the Export permission exports. The figures are the server's; all text goes in with textContent. An empty
// section or value reads N/A.

import { createExportMenu } from "./exportMenu.ts";
import type { GateExportFormat } from "./exportMenuModel.ts";
import {
  CHARGES_FAILED,
  CHARGES_NA,
  DISPUTE_FAILED,
  DISPUTE_NOTE_MAX,
  DISPUTE_REASON_OPTIONS,
  balanceCell,
  centsCell,
  dayCell,
  disputeRequest,
  signedCentsCell,
  textCell,
  type LocationLedgerDto,
  type Tone,
} from "./royaltyChargesModel.ts";
import {
  MONTHLY_SHEETS,
  exportKeyFor,
  mergeLocationRow,
  type MonthlyRow,
} from "./royaltyMonthlyModel.ts";
import { renderMonthlyTable } from "./royaltyMonthlyTable.ts";
import {
  monthLabel,
  monthOptions,
  type RoyaltyStatementDto,
} from "./royaltyStatementModel.ts";

export interface LocationRoyaltyView {
  /** Null for a location that is not a franchise location: the page reads N/A. */
  statement: RoyaltyStatementDto | null;
  /** Null when the location has no ledger; `failed` says the read itself went wrong. */
  ledger: LocationLedgerDto | null;
  ledgerFailed: boolean;
  /** The server's default month, which the picker is built around. */
  defaultMonth: string;
}

export interface LocationRoyaltyDeps {
  onMonth: (month: string) => void;
  /** For the Export menu's Google Sheet item: the one question to our server about whether Google is configured. */
  googleSession: () => { apiBaseUrl: string; tenantId: string } | undefined;
  /** Files a dispute; resolves with a plain message when it did not work. */
  fileDispute: (
    chargeId: string,
    body: { reason: string; note?: string },
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
  /** Reads the page again after a dispute was filed. */
  reload: () => void;
  download: (
    format: GateExportFormat,
    sheet: string | undefined,
    month: string,
    status: HTMLElement,
  ) => Promise<void>;
  /** The session holds admin.payments.onboard. */
  canDispute: () => boolean;
  /** The session holds report.export. */
  canExport: () => boolean;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: { className?: string; text?: string; testId?: string } = {},
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (options.className !== undefined) node.className = options.className;
  if (options.text !== undefined) node.textContent = options.text;
  if (options.testId !== undefined) node.setAttribute("data-testid", options.testId);
  return node;
}

function chip(text: string, tone: Tone, testId: string): HTMLElement {
  const node = el("span", { className: `so-badge so-badge-${tone}`, testId });
  const dot = el("span", { className: "so-badge-dot" });
  dot.setAttribute("aria-hidden", "true");
  node.append(dot, text);
  return node;
}

function td(text: string, testId: string, right = false): HTMLTableCellElement {
  const cell = el("td", { text, testId });
  if (right) cell.className = "num";
  return cell;
}

function listTable(
  testId: string,
  heads: readonly string[],
): { table: HTMLTableElement; body: HTMLTableSectionElement } {
  const node = el("table", { className: "report-table rc-table", testId });
  const head = el("thead");
  const row = el("tr");
  for (const label of heads) {
    const th = el("th", { text: label });
    th.scope = "col";
    row.append(th);
  }
  head.append(row);
  const body = el("tbody");
  node.append(head, body);
  return { table: node, body };
}

function section(title: string, testId: string): HTMLElement {
  const node = el("section", { className: "rc-section", testId });
  node.append(el("h4", { className: "rc-section-title", text: title }));
  return node;
}

function na(testId: string): HTMLElement {
  return el("p", { className: "rc-na", text: CHARGES_NA, testId });
}

export function renderLocationRoyalty(
  host: HTMLElement,
  view: LocationRoyaltyView | null,
  deps: LocationRoyaltyDeps,
): void {
  host.replaceChildren();
  const card = el("div", { className: "card rc-panel", testId: "rs-card" });
  host.append(card);
  const statement = view?.statement ?? null;
  if (view === null || statement === null) {
    // UIFIX1 (SF-3): a location with no royalty says so in one sentence, never the letters N/A in a tall empty card.
    card.append(el("p", { className: "rc-empty-sentence", text: "This is not a franchise location, so it has no royalty.", testId: "rs-na" }));
    return;
  }

  // ---- month and Export
  const bar = el("div", { className: "rc-head" });
  const picker = el("div", { className: "rc-picker" });
  const label = el("label", { className: "field-label", text: "Month" });
  label.htmlFor = "rs-month";
  const select = el("select", { className: "field-input", testId: "rs-month" });
  select.id = "rs-month";
  for (const option of monthOptions(view.defaultMonth)) {
    const node = document.createElement("option");
    node.value = option.value;
    node.textContent = option.label;
    select.append(node);
  }
  select.value = statement.month;
  select.addEventListener("change", () => deps.onMonth(select.value));
  picker.append(label, select);
  bar.append(picker);
  const actions = el("div", { className: "rc-actions" });
  const status = el("span", { className: "rc-download-status", testId: "rc-download-status" });
  status.setAttribute("role", "status");
  if (deps.canExport()) {
    const exportButton = el("button", { className: "btn-secondary", text: "Export", testId: "rc-export" });
    exportButton.type = "button";
    actions.append(exportButton, status);
    createExportMenu({
      trigger: exportButton,
      testIdPrefix: "rc-export",
      menuActive: () => true,
      onPlain: () => undefined,
      sheetChoices: () => MONTHLY_SHEETS,
      googleSheet: deps.googleSession,
      onPick: (format, sheetId) => {
        status.textContent = "";
        void deps.download(format, exportKeyFor(sheetId), statement.month, status);
      },
    });
  }
  bar.append(actions);
  card.append(bar);

  // ---- the one row
  const rows = mergeLocationRow(statement, view.ledger);
  const table = el("table", { className: "report-table rm-table", testId: "rs-table" });
  const tableWrap = el("div", { className: "rc-section" });
  tableWrap.append(table);
  let formAnchor: HTMLElement = tableWrap;
  renderMonthlyTable(table, rows, {
    prefix: "rm",
    showFranchisee: false,
    showTotals: false,
    canResolve: false,
    onResolve: () => undefined,
    canDispute: deps.canDispute(),
    onDispute: (row) => openForm(formAnchor, row, statement.month),
  });
  card.append(tableWrap);
  if (view.ledgerFailed) {
    card.append(el("p", { className: "field-error", text: CHARGES_FAILED, testId: "rc-failed" }));
    return;
  }
  const ledger = view.ledger;
  if (ledger === null) return;

  // ---- adjustments
  const adjustments = section("Adjustments", "rc-adjustments");
  if (ledger.adjustments.length === 0) {
    adjustments.append(na("rc-adjustments-na"));
  } else {
    const t = listTable("rc-adjustments-table", ["Month", "Kind", "Amount", "Netted", "Carried", "Netted in"]);
    for (const a of ledger.adjustments) {
      const row = el("tr", { testId: "rc-adjustment-row" });
      row.append(
        el("th", { text: a.month, testId: "rc-adjustment-month" }),
        td(a.kindLabel, "rc-adjustment-kind"),
        td(signedCentsCell(a.deltaCents), "rc-adjustment-amount", true),
        td(centsCell(a.appliedCents), "rc-adjustment-applied", true),
        td(balanceCell(a.remainingCents), "rc-adjustment-remaining", true),
        td(a.nettedIn.length === 0 ? CHARGES_NA : a.nettedIn.join(", "), "rc-adjustment-netted"),
      );
      t.body.append(row);
    }
    adjustments.append(t.table);
  }

  // ---- disputes
  const disputes = section("Disputes", "rc-disputes");
  if (ledger.disputes.length === 0) {
    disputes.append(na("rc-disputes-na"));
  } else {
    const t = listTable("rc-disputes-table", [
      "Month",
      "Status",
      "Reason",
      "Filed",
      "Resolved",
      "Credit",
      "Franchisor note",
    ]);
    for (const d of ledger.disputes) {
      const row = el("tr", { testId: "rc-dispute-row" });
      const statusCell = el("td");
      statusCell.append(
        chip(
          d.statusLabel,
          d.status === "open" ? "warn" : d.status === "adjusted" ? "good" : "neutral",
          "rc-dispute-status",
        ),
      );
      row.append(
        el("th", { text: d.month, testId: "rc-dispute-month" }),
        statusCell,
        td(d.reasonLabel, "rc-dispute-reason"),
        td(dayCell(d.openedAt), "rc-dispute-filed"),
        td(dayCell(d.resolvedAt), "rc-dispute-resolved"),
        td(d.creditCents === null ? CHARGES_NA : centsCell(d.creditCents), "rc-dispute-credit", true),
        td(textCell(d.resolutionNote), "rc-dispute-note"),
      );
      t.body.append(row);
    }
    disputes.append(t.table);
  }
  card.append(adjustments, disputes);
  formAnchor = disputes;

  function openForm(anchor: HTMLElement, row: MonthlyRow, month: string): void {
    const chargeId = row.chargeId;
    if (chargeId === null) return;
    card.querySelector("[data-testid='rc-dispute-form']")?.remove();
    const form = el("form", { className: "rc-form", testId: "rc-dispute-form" });
    form.setAttribute("aria-label", `Dispute the ${monthLabel(month)} charge`);
    form.append(el("h4", { className: "rc-section-title", text: `Dispute ${monthLabel(month)}` }));
    const reasonLabel = el("label", { className: "field-label", text: "Reason" });
    reasonLabel.htmlFor = "rc-dispute-reason";
    const reason = el("select", { className: "field-input", testId: "rc-dispute-reason" });
    reason.id = "rc-dispute-reason";
    for (const option of DISPUTE_REASON_OPTIONS) {
      const node = document.createElement("option");
      node.value = option.code;
      node.textContent = option.label;
      reason.append(node);
    }
    const noteLabel = el("label", { className: "field-label", text: "Note" });
    noteLabel.htmlFor = "rc-dispute-note-input";
    const note = el("textarea", { className: "field-input", testId: "rc-dispute-note-input" });
    note.id = "rc-dispute-note-input";
    note.maxLength = DISPUTE_NOTE_MAX;
    note.rows = 3;
    const error = el("p", { className: "field-error", testId: "rc-dispute-error" });
    error.setAttribute("role", "alert");
    const submit = el("button", { className: "btn-primary", text: "File dispute", testId: "rc-dispute-submit" });
    submit.type = "submit";
    const cancel = el("button", { className: "btn-secondary", text: "Cancel", testId: "rc-dispute-cancel" });
    cancel.type = "button";
    cancel.addEventListener("click", () => form.remove());
    const buttons = el("div", { className: "rc-actions" });
    buttons.append(submit, cancel);
    form.append(reasonLabel, reason, noteLabel, note, error, buttons);
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const request = disputeRequest(reason.value, note.value);
      if (!request.ok) {
        error.textContent = request.message;
        return;
      }
      submit.disabled = true;
      error.textContent = "";
      void deps.fileDispute(chargeId, request.body).then((outcome) => {
        if (outcome.ok) {
          deps.reload();
        } else {
          submit.disabled = false;
          error.textContent = outcome.message === "" ? DISPUTE_FAILED : outcome.message;
        }
      });
    });
    anchor.append(form);
    reason.focus();
  }
}
